import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { equipmentCountForModelState, modelState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import type { Brand, BrandId, Model, ModelId, ModelMergePreview } from '@rock/types';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { liveBrandOf } from './modelJoin';
import { mergeModels } from './modelMergeApi';

const log = logger('MergeModelDialog');

/**
 * Merge this model into another that shares its number: a duplicate minted beside its sibling, or one
 * filed under a type since re-filed. The preview is the same call with `dryRun`, so the counts are what
 * the confirm will do, and a refusal (the two fail each other's gates) stays on screen with its reason.
 * The merge then lands on the surviving model's page.
 */
export const MergeModelDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    loser: Model;
    winnerId: ModelId;
    brandsById: Map<BrandId, Brand>;
}> = ({ open, onOpenChange, loser, winnerId, brandsById }) => {
    const navigate = useNavigate();
    const winner = useAsyncAtomValue(useMemo(() => modelState(winnerId), [winnerId]));
    const winnerUnits = useAsyncAtomValue(useMemo(() => equipmentCountForModelState(winnerId), [winnerId]));
    const [preview, setPreview] = useState<ModelMergePreview>();
    const [refusal, setRefusal] = useState<string>();
    const [previewing, setPreviewing] = useState(false);
    const [merging, setMerging] = useState(false);

    useEffect(() => {
        if (!open || merging) return;
        let cancelled = false;
        setPreview(undefined);
        setRefusal(undefined);
        setPreviewing(true);
        mergeModels({ loserId: loser.id, winnerId, dryRun: true })
            .then((result) => {
                if (cancelled) return;
                if (result.status === 'preview') setPreview(result.preview);
                else if (result.status === 'refused') setRefusal(result.reason);
            })
            .catch((err) => {
                if (cancelled) return;
                log.error('Merge preview failed', { err, loserId: loser.id, winnerId });
                setRefusal('Unable to preview the merge. Try again in a moment.');
            })
            .finally(() => !cancelled && setPreviewing(false));
        return () => {
            cancelled = true;
        };
    }, [open, merging, loser.id, winnerId]);

    const handleMerge = async () => {
        setMerging(true);
        try {
            const result = await mergeModels({ loserId: loser.id, winnerId, dryRun: false });
            if (result.status === 'merged') {
                log.info('Merged model', { loserId: loser.id, winnerId });
                toast.success(`Merged into ${winner?.modelDisplay ?? winnerId}`, {
                    description: `${result.preview.units} unit(s) moved; ${result.preview.documents} document link(s) carried over.`,
                });
                onOpenChange(false);
                navigate(`/products/models/${encodeURIComponent(winnerId)}`);
            } else if (result.status === 'refused') {
                setRefusal(result.reason);
            }
        } catch (err) {
            log.error('Model merge failed', { err, loserId: loser.id, winnerId });
            toast.error('Merge failed part-way', { description: 'Run the same merge again to pick up what is left.' });
        } finally {
            setMerging(false);
        }
    };

    const busy = previewing || merging;
    return (
        <Dialog open={open} onOpenChange={(next) => !merging && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Merge this model into {winner?.modelDisplay ?? '…'}</DialogTitle>
                    <DialogDescription>
                        Every unit moves to the surviving model, which also gains the spellings, known-as names, manual
                        links, and the brand or research it lacked. This model becomes a tombstone that points at it;
                        its recall matches go, and its units are decided against the survivor's recalls.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <Side label="This model" model={loser} brandsById={brandsById} units={preview?.units} />
                    <Side label="Survivor" model={winner} brandsById={brandsById} units={winnerUnits} />
                </div>
                {refusal ? (
                    <p className="text-sm text-destructive">{refusal}</p>
                ) : preview ? (
                    <PreviewList preview={preview} />
                ) : (
                    <div className="section-description">Counting…</div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={merging}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => void handleMerge()} disabled={busy || !preview}>
                        {merging ? 'Merging…' : 'Merge'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Side: FC<{
    label: string;
    model: Model | undefined;
    brandsById: Map<BrandId, Brand>;
    units: number | undefined;
}> = ({ label, model, brandsById, units }) => {
    const brand = model ? liveBrandOf(model.brandId, brandsById) : undefined;
    return (
        <div className="flex flex-col gap-1 rounded-md border p-2">
            <span className="text-xs font-medium text-muted-foreground">{label}</span>
            <span className="font-mono font-medium">{model?.modelDisplay ?? '…'}</span>
            <span>{brand ? `${brand.canonicalName} (${brand.reviewStatus})` : 'no brand'}</span>
            {model && <EquipmentTypeBadge typeId={model.typeId} />}
            <span className="text-muted-foreground">{units === undefined ? '…' : `${units} unit(s)`}</span>
        </div>
    );
};

const PreviewList: FC<{ preview: ModelMergePreview }> = ({ preview }) => (
    <ul className="space-y-1 text-sm">
        <li>{preview.units} unit(s) move, and each is decided against the survivor's recalls.</li>
        <li>{preview.recallMatches} recall match(es) of this model go.</li>
        <li>
            {preview.documents} document link(s), {preview.knownAs} known-as name(s) and {preview.spellings} spelling(s)
            carry over.
        </li>
        {preview.carriesBrand && <li>The survivor has no brand, so it takes this model's.</li>}
        {preview.brand === 'replaced' && (
            <li className="text-amber-700">
                The two carry different brands. No unit names this model's, so the survivor's brand stands and the moved
                units show it. This model's research stays behind; its manual links, found under its brand, still carry
                over.
            </li>
        )}
        {preview.carriesResearch && <li>The survivor has no research, so it takes this model's.</li>}
        {preview.typeAffinity === 'family' && (
            <li className="text-amber-700">The two are filed under different types in one family.</li>
        )}
    </ul>
);
